import { create, StoreApi, UseBoundStore } from 'zustand';
import { createBuildSlice } from '@/stores/build';
import { devtools, persist } from 'zustand/middleware';
import { enableArrayMethods } from 'immer';
import { immer } from 'zustand/middleware/immer';
import { createPlanSlice } from './plan';
import { createPlannerSlice } from './planner';
import { createProjectSlice } from './project';
import { createRootActions } from './rootActions';

import { BuildSlice } from '@/types/stores/build';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';

// Non-mutating array methods in immer are faster, but also do not create a proxy object to update state.
// https://github.com/immerjs/immer/releases/tag/v11.1.0
enableArrayMethods();

export const useBoundStore = create<BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions>()(
  devtools(
    persist(
      immer((...a) => ({
        ...createBuildSlice(...a),
        ...createPlanSlice(...a),
        ...createPlannerSlice(...a),
        ...createProjectSlice(...a),
        ...createRootActions(...a),
      })),
      {
        name: 'airchef-prefs',
        version: 5,
        partialize: (state) => ({
          planner: {
            prefs: state.planner.prefs,
          },
        }),
        merge: (persisted, current) => {
          return {
            ...current,
            planner: {
              ...current.planner,
              prefs: (persisted as any).planner.prefs,
            },
          };
        },
        migrate(persistedState, version) {
          if (version < 1) {
            (persistedState as PlannerSlice).planner.prefs.propertiesGroups = {};
          }
          if (version < 3) {
            // @ts-expect-error rest is set up in ver 4
            (persistedState as PlannerSlice).planner.prefs.summary = { size: 'cozy', columns: {} };
          }
          if (version < 4) {
            (persistedState as PlannerSlice).planner.prefs.summary.configs = {};
          }
          if (version < 5) {
            (persistedState as PlannerSlice).planner.prefs.warnings = {};
          }
          return persistedState;
        },
      },
    ),
    { enabled: import.meta.env.DEV },
    // { enabled: false },
  ),
);

// Zustand loses state with hot reload, most likely due to ciruclar depts.
// Not sure where the circular dep (if any) is at the moment.
// There is a tool on npm - madge - that would give a nice dependency graph out to find those as well,
// except it seems to ignore tsconfig path aliases (open issue about it: https://github.com/pahen/madge/issues/399)
// So we'll make due and use this function keep the state in memory between reloads.
export const zustandHmrFix = (
  useStore: UseBoundStore<StoreApi<BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions>>,
) => {
  if (import.meta.hot) {
    const savedState = import.meta.hot?.data?.['rootStore'];
    if (savedState) {
      useStore.setState(mergeSavedStateToStore(savedState, useStore.getState()));
    }

    useStore.subscribe((state) => {
      // Can't copy the state directly as we want to have the functions in the store to update
      // with HMR. So, this extracts the data only to keep between reloads.
      const toKeep = extractStateToKeep(state);
      import.meta.hot!.data['rootStore'] = toKeep;
    });

    import.meta.hot!.accept((newModule) => {
      if (newModule) {
        const savedState = import.meta.hot!.data['rootStore'];

        if (savedState) {
          useStore.setState(mergeSavedStateToStore(savedState, useStore.getState()));
        }
      }
    });
  }
};

function extractStateToKeep<T extends object>(state: T): Partial<T> {
  // console.log(state);
  const newState: Partial<T> = {};
  for (const field in state) {
    const fieldType = typeof state[field];
    if (Array.isArray(state[field])) {
      // We can just copy arrays as is, because those are always data, and no functions in them
      newState[field] = structuredClone(state[field]);
    } else if (fieldType === 'object' && state[field] !== null) {
      // @ts-expect-error any
      newState[field] = extractStateToKeep(state[field]);
    } else if (fieldType === 'function') {
      // omit these
    } else {
      newState[field] = state[field];
    }
  }

  return newState;
}

function mergeSavedStateToStore<T extends Record<string, unknown>>(savedState: Partial<T>, store: T): T {
  const mergedState = { ...store };
  for (const field in savedState) {
    const fieldType = typeof savedState[field];
    if (Array.isArray(savedState[field])) {
      // @ts-expect-error any
      mergedState[field] = savedState[field];
    } else if (fieldType === 'object' && savedState[field] !== null) {
      // @ts-expect-error any
      mergedState[field] = mergeSavedStateToStore(
        // @ts-expect-error any
        savedState[field],
        store[field] ?? {},
      );
    } else if (fieldType === 'function') {
      // omit these
    } else {
      // @ts-expect-error any
      mergedState[field] = savedState[field];
    }
  }
  return mergedState;
}

// if (import.meta.hot) {
//   zustandHmrFix(useBoundStore);
// }
